import { Template } from 'app/data';

const TEMPLATES: Template[] = [
  {
    id: 't-000001',
    slug: 'design-a-smart-goal',
    title: 'Design a SMART Goal',
    description:
      'Setting effective and achievable goals is crucial to making progress on them. This activity guides you through defining a goal in a clear, detailed, and achievable way, using the SMART criteria.',
    technical_description:
      "The SMART Goal activity helps therapists and coaches facilitate productive goal setting with clients. SMART goals (Specific, Measurable, Attainable, Realistic, and Time-bound) provide a structured approach that's particularly useful for clients who need clarity and direction in their personal or professional objectives, fostering a sense of purpose and motivation.\n\n### References\n- Bailey, R. R. (2019). Goal setting and action planning for health behavior change. American journal of lifestyle medicine, 13(6), 615-618.",
    type: 'exercise',
    tags: [],
    blocks: [
      {
        id: 'smart-reading',
        type: 'reading',
        heading: 'How to make a SMART Goal',
        content:
          "**SMART** stands for Specific, Measurable, Attainable, Realistic, and Time-bound. Use the process below to turn any goal you have into a clearer and more achievable version.\n1. **Brainstorm**: start by writing down what you want to achieve overall. Don't worry about being perfect. Try and focus on behaviors you want to do more of (e.g. walk twice a week) instead of behaviors you want to avoid (e.g. avoid sweets)\n2. **Get specific:** now edit your goal to be more specific. For example, who is involved, what you want to accomplish, when it will be done, where it will happen, and how you will do it?\n3. **Make it measurable**: determine how you will measure your progress or success. How much, how far, and how long will it take to achieve your goal?\n4. **Ensure it's attainable**: do you have the necessary skills, knowledge, and resources to achieve it? Ask yourself 'Can I achieve this goal?' It's important to be honest here.\n5. **Assess if the goal is realistic**: Is this goal something you are willing and able to work towards? Ask yourself 'Will I achieve this goal?'\n6. **Put a time on it**: Set a timeframe for your goal to make sure you know when you'll evaluate it. When will you start, what are the milestones or stepping stones, and when do you plan to achieve it?",
      },
      {
        id: 'goal',
        type: 'text_input',
        heading: 'Your goal',
        subheading:
          'Use this space to write your goal and update it as you go through the steps above.',
      },
      {
        id: 'goal-evaluation',
        type: 'scale',
        heading: 'How do you feel about your goal?',
        min: 1,
        max: 10,
        min_label: '',
        max_label: '',
        mid_label: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.339Z',
    updated_at: '2024-03-20T22:45:29.339Z',
  },
  {
    id: 't-000002',
    slug: 'thought-record-thoughts-and-emotions',
    title: 'Thought Record: Thoughts and Emotions',
    description:
      'Thoughts and emotions can define our experience over time. This tracker helps track thoughts and emotions, a first step in addressing unhelpful thinking and feeling better.',
    technical_description:
      'Thought Records are essential tools in Cognitive Behavioral Therapy (CBT) for capturing and challenging unhelpful thinking patterns, with roots in the pioneering work of Aaron T. Beck and Albert Ellis, two of the most influential psychologists of the 20th century. They are particularly effective in evaluating automatic thoughts during times of distress and are a popular choice for facilitating belief change.\nHere are some important guidelines when using thought records:\n\n- Regular practice is important for developing a feeling of awareness of negative or dysfunctional thinking.\n- The optimal time for completing a thought record is shortly after experiencing a change in emotions, particularly following an emotional upset, difficult situation, or concern.\n- It can be helpful for therapists and clients to work through example situations that lead to unpleasant feelings together to make sure each element of the thought record exercise is clear.\n\n### References\n- Beck, J. S. (2011). *Cognitive behavior therapy: Basics and beyond*. Guilford Press.\n- McManus, F., Van Doorn, K., & Yiend, J. (2012). *Examining the effects of thought records and behavioral experiments in instigating belief change*. Journal of Behavior Therapy and Experimental Psychiatry, 43(1), 540–547.\n- American Psychological Association (2017). *What is cognitive behavioral therapy?* Clinical Practice Guideline for the Treatment of Posttraumatic Stress Disorder.\n- Widnall, E., Price, A., Trompetter, H., & Dunn, B. D. (2019). *Routine cognitive behavioural therapy for anxiety and depression is more effective at repairing symptoms of psychopathology than enhancing wellbeing*. Cognitive Therapy and Research, 44, 28–39.',
    type: 'exercise',
    tags: ['CBT'],
    blocks: [
      {
        id: 'situation',
        type: 'text_input',
        heading: 'What was the situation or trigger?',
        subheading: '',
      },
      {
        id: 'multi_emotions',
        type: 'multi_entry',
        heading: 'What emotions did you experience?',
        blocks: [
          [
            {
              id: 'emotion_select',
              type: 'select',
              heading: 'Choose an emotion',
              options: {
                Happiness: [
                  'Happy',
                  'Satisfied',
                  'Curious',
                  'Proud',
                  'Confident',
                  'Respected',
                  'Powerful',
                  'Thankful',
                  'Peaceful',
                  'Loving',
                  'Trusting',
                  'Hopeful',
                  'Inspired',
                ],
                Sadness: [
                  'Sad',
                  'Lonely',
                  'Vulnerable',
                  'Powerless',
                  'Grief',
                  'Hopeless',
                  'Regretful',
                  'Guilty',
                  'Ashamed',
                  'Empty',
                ],
                Fear: [
                  'Anxious',
                  'Scared',
                  'Overwhelmed',
                  'Helpless',
                  'Insecure',
                  'Worthless',
                  'Excluded',
                  'Nervous',
                  'Threatened',
                ],
                Anger: [
                  'Angry',
                  'Resentful',
                  'Furious',
                  'Jealous',
                  'Hostile',
                  'Annoyed',
                  'Frustrated',
                  'Bitter',
                  'Skeptical',
                  'Numb',
                ],
                Surprise: [
                  'Surprised',
                  'Excited',
                  'Shocked',
                  'Amazed',
                  'Confused',
                ],
                Disgust: [
                  'Embarrassed',
                  'Humiliated',
                  'Self-Conscious',
                  'Insecure',
                  'Disappointed',
                ],
                Other: ['Bad', 'Tired', 'Overwhelmed', 'Bored'],
              },
            },
            {
              id: 'emotion_strength',
              type: 'scale',
              heading: 'Strength of emotion',
              min: 1,
              max: 10,
              min_label: 'Very weak',
              max_label: 'Very strong',
              mid_label: '',
            },
          ],
        ],
      },
      {
        id: 'thoughts',
        type: 'text_input',
        heading: 'What thoughts did you have about the situation?',
        subheading: '',
      },
      {
        id: 'unhelpful-thought',
        type: 'text_input',
        heading: 'Which thought was most unhelpful?',
        subheading: '',
      },
      {
        id: 'belief',
        type: 'scale',
        heading: 'How much do you believe your most unhelpful thought?',
        min: 1,
        max: 10,
        min_label: 'Very unsure',
        max_label: 'Very sure',
        mid_label: 'Some confidence',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.340Z',
    updated_at: '2024-03-20T22:45:29.340Z',
  },
  {
    id: 't-000003',
    slug: 'thought-record-challenging-and-reframing',
    title: 'Thought Record: Challenging and Reframing',
    description:
      'This exercises helps understand and reframe unhelpful thoughts by asking us to honestly look at the evidence for the thoughts and thinking of an alternative thought.',
    technical_description:
      'Thought reframing is an essential tools in Cognitive Behavioral Therapy (CBT) for addressing unhelpful thinking patterns. This activity goes beyond tracking thoughts and emotions and guides the process of reframing unhelpful thoughts by looking for evidence in favor and against them.\nHere are some important guidelines when using thought records:\n\n- Regular practice is important for developing a feeling of awareness of negative or dysfunctional thinking.\n- The optimal time for completing a thought record is shortly after experiencing a change in emotions, particularly following an emotional upset, difficult situation, or concern.\n- It can be helpful for therapists and clients to work through example situations that lead to unpleasant feelings together to make sure each element of the thought record exercise is clear.\n\n### References\n- Beck, J. S. (2011). *Cognitive behavior therapy: Basics and beyond*. Guilford Press.\n- McManus, F., Van Doorn, K., & Yiend, J. (2012). *Examining the effects of thought records and behavioral experiments in instigating belief change*. Journal of Behavior Therapy and Experimental Psychiatry, 43(1), 540–547.\n- American Psychological Association (2017). *What is cognitive behavioral therapy?* Clinical Practice Guideline for the Treatment of Posttraumatic Stress Disorder.\n- Widnall, E., Price, A., Trompetter, H., & Dunn, B. D. (2019). *Routine cognitive behavioural therapy for anxiety and depression is more effective at repairing symptoms of psychopathology than enhancing wellbeing*. Cognitive Therapy and Research, 44, 28–39.',
    type: 'exercise',
    tags: ['CBT'],
    blocks: [
      {
        id: 'situation',
        type: 'text_input',
        heading: 'What was the situation or trigger?',
        subheading:
          'Describe the situation or trigger that led to the thought.',
      },
      {
        id: 'pre-mood',
        type: 'scale',
        heading: 'How are you currently feeling about the situation?',
        min: 1,
        max: 10,
        min_label: 'Very unhappy',
        max_label: 'Very happy',
        mid_label: '',
      },
      {
        id: 'multi_emotions',
        type: 'multi_entry',
        heading: 'What emotions did you experience?',
        blocks: [
          [
            {
              id: 'emotion_select',
              type: 'select',
              heading: 'Choose an emotion',
              options: {
                Happiness: [
                  'Happy',
                  'Satisfied',
                  'Curious',
                  'Proud',
                  'Confident',
                  'Respected',
                  'Powerful',
                  'Thankful',
                  'Peaceful',
                  'Loving',
                  'Trusting',
                  'Hopeful',
                  'Inspired',
                ],
                Sadness: [
                  'Sad',
                  'Lonely',
                  'Vulnerable',
                  'Powerless',
                  'Grief',
                  'Hopeless',
                  'Regretful',
                  'Guilty',
                  'Ashamed',
                  'Empty',
                ],
                Fear: [
                  'Anxious',
                  'Scared',
                  'Overwhelmed',
                  'Helpless',
                  'Insecure',
                  'Worthless',
                  'Excluded',
                  'Nervous',
                  'Threatened',
                ],
                Anger: [
                  'Angry',
                  'Resentful',
                  'Furious',
                  'Jealous',
                  'Hostile',
                  'Annoyed',
                  'Frustrated',
                  'Bitter',
                  'Skeptical',
                  'Numb',
                ],
                Surprise: [
                  'Surprised',
                  'Excited',
                  'Shocked',
                  'Amazed',
                  'Confused',
                ],
                Disgust: [
                  'Embarrassed',
                  'Humiliated',
                  'Self-Conscious',
                  'Insecure',
                  'Disappointed',
                ],
                Other: ['Bad', 'Tired', 'Overwhelmed', 'Bored'],
              },
            },
            {
              id: 'emotion_strength',
              type: 'scale',
              heading: 'Strength of emotion',
              min: 1,
              max: 10,
              min_label: 'Very weak',
              max_label: 'Very strong',
              mid_label: '',
            },
          ],
        ],
      },
      {
        id: 'thought',
        type: 'text_input',
        heading: 'What unhelpful thoughts did you have?',
        subheading:
          'Describe the unhelpful thoughts you had during the situation.',
      },
      {
        id: 'evidence-supporting',
        type: 'text_input',
        heading: 'Evidence supporting your unhelpful thoughts',
        subheading:
          'What are the true facts of the situation which support your unhelpful thought?',
      },
      {
        id: 'evidence-against',
        type: 'text_input',
        heading: 'Evidence against your unhelpful thoughts',
        subheading: 'What facts oppose the unhelpful thoughts?',
      },
      {
        id: 'alternative',
        type: 'text_input',
        heading: 'Alternative thought',
        subheading:
          'Take a deep breath. What would someone else say after seeing the evidence you have written? Is there another way of seeing this situation that is more realistic?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.340Z',
    updated_at: '2024-03-20T22:45:29.340Z',
  },
  {
    id: 't-000004',
    slug: 'free-form-journal',
    title: 'Free Form Journal',
    description:
      'Writing down our thoughts can be extremely helpful in understanding our thoughts, getting emotions out, and getting things off our mind. Use this space to write however is helpful for you.',
    technical_description: '',
    type: 'exercise',
    tags: ['Journaling'],
    blocks: [
      {
        id: 'journal',
        type: 'text_input',
        heading: "What's on your mind?",
        subheading: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.340Z',
    updated_at: '2024-03-20T22:45:29.340Z',
  },
  {
    id: 't-000005',
    slug: 'gratitude-journal',
    title: 'Gratitude Journal',
    description:
      "A gratitude journal is a simple way to refocus your mind on positive things in your life. Take a moment to write down things you're grateful for using some of the prompts below.",
    technical_description:
      'The gratitude journal is an evidence-based intervention that helps focus on positive aspects of life and enhances psychological wellbeing. This practice aligns with concepts in positive psychology and self-actualization (Maslow, 1981). Regularly expressing gratitude is associated with increased happiness, life satisfaction, meaningfulness, and productivity.\n### Evidence \nEmmons and McCullough (2003) found that participants who journaled about gratitude reported more optimism, physical exercise, and fewer symptoms of illness compared to those journaling about annoyances or neutral events. This intervention fosters a general sense of happiness, more acts of kindness, better sleep, and a refreshed feeling in the morning.\n### References\n- Dickens, L. R. (2017). Using gratitude to promote positive change: A series of meta-analyses investigating the effectiveness of gratitude interventions. Basic and Applied Social Psychology, 39(4), 193–208.\n- Emmons, R. A., & Crumpler, C. A. (2000). Gratitude as a human strength: Appraising the evidence. Journal of Social and Clinical Psychology, 19(1), 56–69.\n- Emmons, R. A., & McCullough, M. E. (2003). Counting blessings versus burdens: An experimental investigation of gratitude and subjective wellbeing in daily life. Journal of Personality and Social Psychology, 84(2), 377–389.  ',
    type: 'exercise',
    tags: ['Journaling'],
    blocks: [
      {
        id: 'journal',
        type: 'text_input',
        heading: 'What are you grateful for today?',
        subheading:
          'Consider something good that happened today, a person who made a difference in your life, or even something simple like the weather.',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.341Z',
    updated_at: '2024-03-20T22:45:29.341Z',
  },
  {
    id: 't-000006',
    slug: 'letter-to-future-self',
    title: 'Letter to Future Self',
    description:
      'In this activity, you will write a letter to your future self. Imagine speaking to a future version of you, perhaps a year or more from now. What hopes, dreams, or advice do you want to share? This can be a powerful way to acknowledge your current feelings, aspirations, and the progress you hope to make.',
    technical_description:
      "The 'Letter to Your Future Self' activity is a therapeutic tool that encourages clients to engage in self-reflection and future-oriented thinking. This exercise aids in the development of a hopeful perspective and allows clients to articulate personal goals and the steps necessary to achieve them. It can be particularly effective in fostering a sense of control and optimism in clients dealing with anxiety, depression, or life transitions.  \n\nResearch suggests that expressive writing, such as writing letters to oneself, can lead to improved mood, reduced stress, and enhanced well-being (Pennebaker & Beall, 1986). Additionally, setting goals and envisioning a positive future self can be linked to increased motivation and a sense of purpose, as noted by Sheldon and Lyubomirsky (2006). This exercise combines these principles, providing a space for clients to express emotions, set intentions, and envision a positive and achievable future.\n\n### **References**\n1. Pennebaker, J. W., & Beall, S. K. (1986). Confronting a traumatic event: Toward an understanding of inhibition and disease. *Journal of Abnormal Psychology, 95*(3), 274-281.\n2. Sheldon, K. M., & Lyubomirsky, S. (2006). Achieving sustainable gains in happiness: Change your actions, not your circumstances. *Journal of Happiness Studies, 7*(1), 55-86.",
    type: 'exercise',
    tags: ['Journaling'],
    blocks: [
      {
        id: 'preparation',
        type: 'text_input',
        heading: 'Perpare and brainstorm',
        subheading:
          "You might consider focusing on aspects of your life that are important to you, your hopes for the future, challenges you have, or writing a message of encouragement. Remember, there's no need to be perfect.",
      },
      {
        id: 'letter',
        type: 'text_input',
        heading: 'Dear self...',
        subheading: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.341Z',
    updated_at: '2024-03-20T22:45:29.341Z',
  },
  {
    id: 't-000007',
    slug: 'common-cognitive-distortions',
    title: 'Common Cognitive Distortions',
    description:
      'Cognitive distortions are patterns of thinking that are often negative and inaccurate. Understanding these distortions can help you identify and challenge them in your daily life, leading to healthier thoughts and behaviors.',
    technical_description:
      'Cognitive distortions are inaccurate thoughts that can reinforce negative thinking and emotions. They are a common occurrence in various psychological conditions like anxiety and depression. This activity aims to educate clients about these distortions and help them develop skills to recognize and challenge such thoughts, ultimately fostering healthier cognitive patterns.\n\n### References\n- Beck, A. T. (1976). Cognitive Therapy and the Emotional Disorders.\n- Burns, D. D. (1980). Feeling Good: The New Mood Therapy.',
    type: 'education',
    tags: ['CBT'],
    blocks: [
      {
        id: 'cognitive-distortions',
        type: 'reading',
        heading: 'What are cognitive distortions?',
        content:
          "Cognitive distortions are like mental filters or lenses that skew our perception of reality, often in a negative and unhelpful way. Imagine wearing a pair of glasses that distort the shapes and colors around you - this is similar to how cognitive distortions affect our understanding of what happens in our lives. These distortions are patterns of thinking that convince us of something that isn't really true. For example, if you make a small mistake at work and think, “I'm terrible at my job”, that's a distortion! These misleading thoughts can lead to a range of emotional and behavioral issues, such as anxiety, low self-esteem, or feelings of hopelessness. They often magnify our faults and failures, and minimize or ignore our strengths and successes. By recognizing and challenging these distortions, we can begin to see things in a more balanced and realistic way, leading to better mental health and well-being.",
      },
      {
        id: 'cognitive-distortions-list',
        type: 'reading',
        heading: 'Common cognitive distortions',
        content:
          "1. **Catastrophic Thinking (catastrophizing):** This is when you expect the worst to happen, even if it's unlikely. For instance, if you send a text and don't get an immediate reply, you might start thinking, 'They're angry with me, or something terrible has happened,' when in reality, they might just be busy.\n2. **Discounting the Positive:** This is like ignoring the good things and focusing only on the bad. Imagine you cooked a meal and everyone loved it, but you think, 'It was just luck; I'm not really a good cook.' You're not giving yourself credit for your effort and skill.\n3. **Emotional Reasoning:** Here, you believe something is true just because you feel it strongly. For example, if you feel overwhelmed and think, 'I can't handle anything,' you're assuming your feelings accurately reflect your capabilities, which might not be the case.\n4. **Labeling and Mislabeling:** This is when you unfairly label yourself or others based on one thing that happened. Say you forgot an appointment and you think, 'I'm such an irresponsible person.' That's a harsh label based on a single incident.\n5. **Mental Filtering:** This happens when you only see the negative side of things and ignore the positive. For instance, if you had a day where you completed most of your tasks but missed one, and you think, 'I've had a terrible day,' you're filtering out the good parts.\n6. **Jumping to Conclusions:** This is when you make a decision without enough evidence. If a friend doesn’t invite you to a party and you instantly think, 'They don't like me anymore,' you're jumping to a conclusion without knowing the whole story.\n7. **Overgeneralization:** This is when you take one thing that happened and assume it will happen every time. Like if you had a relationship that didn't work out and you think, 'All my relationships are doomed to fail,' you're applying one experience to all future situations.\n8. **Personalization:** This means thinking that everything people do or say is some kind of reaction to you. If your friend is in a bad mood and you think, 'It must be because of something I did,' you're making it about you when it might not be.\n9. **Black-and-White Thinking:** This is seeing things as only good or bad, with no middle ground. If you’re not perfect at something, you might think, 'I'm a total failure,' ignoring the fact that most things are somewhere in between.\n10. **“Should” Statements:** This is about having a strict list of what you and others should or shouldn't do, leading to disappointment. For example, thinking, 'I should always be happy,' is unrealistic and can make you feel like a failure when you're just having a normal bad day.\n  \nUnderstanding these distortions is the first step in changing them. By recognizing when your thoughts might be distorted, you can start to see things in a more balanced and realistic way. This helps in reducing unnecessary stress and improving overall mental well-being.",
      },
      {
        id: 'recognize-distortions',
        type: 'text_input',
        heading:
          "What is one example of a cognitive distortion you've observed in your own thinking?",
        subheading: '',
      },
      {
        id: 'takeaways',
        type: 'text_input',
        heading: 'Your takeaways',
        subheading: "What's one thing you want to take away from this reading?",
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.341Z',
    updated_at: '2024-03-20T22:45:29.341Z',
  },
  {
    id: 't-000008',
    slug: 'abc-model-of-cbt',
    title: 'ABC model of CBT',
    description:
      'We react to activating events and situations all the time. Use this approach to break down and understand events, behaviors, and consequences that you experience with the ABC model, which stands for Activating Event, Belief, and Consequence.',
    technical_description:
      'The ABC model is an integral part of CBT, offering a straightforward way for clients to understand the connection between their thoughts, feelings, and behaviors. It empowers clients to change their reactions to their environment and thus their emotional and behavioral outcomes.',
    type: 'education',
    tags: ['CBT'],
    blocks: [
      {
        id: 'abc-reading',
        type: 'reading',
        heading: 'How the ABC model works',
        content:
          "**Activating Event (A)**: This is an external event or internal thought that triggers our reaction. It could be anything from a comment from a colleague to an internal thought about oneself.\n2. **Belief (B)**: Following the event, we form a belief about it. This belief could be rational or irrational, affecting how we interpret the event. For example, if someone doesn’t return your call, you might believe they are ignoring you (an irrational belief) or that they are simply busy (a rational belief).\n3. **Consequence (C)**: Our belief about the event leads to emotional or behavioral consequences. If you believe someone is ignoring you, you might feel rejected or angry. If you believe they’re just busy, you might feel understanding and calm.\n\nIt's important to understand that it’s not the activating event itself that causes our emotional and behavioral responses, but our beliefs about the event. Changing these beliefs, especially the irrational ones, can change our reactions and consequences.\n\n# Examples\n### Dealing with Anger\nJohn, a hard-working employee, faces an upsetting situation at his workplace. His boss criticizes his project in front of his colleagues.\n- **Activating Event (A)**: John’s boss criticizes his work.\n- **Belief (B)**: John believes he is a failure and fears losing his job.\n- **Consequence (C)**: He feels anxious, demotivated, and starts avoiding his colleagues, leading to a decline in his performance.\n  \nIn therapy, John learns to challenge these beliefs. He recognizes that criticism is not an indication of failure but an opportunity for growth. This shift in belief changes his emotional response, making him more resilient to criticism and improving his work performance.\n\n### Dealing with Depression\nImagine a woman who suffers from black-and-white thinking. She makes a minor mistake at work and immediately labels herself as a complete failure.\n- **Activating Event (A)**: The client makes a mistake.\n- **Belief (B)**: She believes she is a failure and not good at anything.\n- **Consequence (C)**: This leads to feelings of worthlessness and depressive symptoms.\n  \nWith the help of her counselor, she learns to dispute these irrational beliefs. She acknowledges that making a mistake doesn’t define her worth or competence. This new way of thinking helps her cope with mistakes more healthily, reducing her depressive symptoms.\n\n## Conclusion\nThe ABC model in CBT offers a framework for understanding how our interpretation of events, not the events themselves, shapes our emotional and behavioral responses. By identifying and challenging irrational beliefs, clients can significantly improve their emotional well-being and behavioral responses to various life situations.",
      },
      {
        id: 'abc-diagram',
        type: 'image',
        heading:
          'Activating events, behaviors, and consequences can all affect each other',
        url: 'https://iveronicawalsh.files.wordpress.com/2012/04/abcd-imagery-slide.jpg',
      },
      {
        id: 'takeaways',
        type: 'text_input',
        heading: 'Your takeaways',
        subheading: 'How does the ABC concept apply to your life?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.342Z',
    updated_at: '2024-03-20T22:45:29.342Z',
  },
  {
    id: 't-000009',
    slug: 'resisting-urges-and-cravings-learn-the-deads-strat',
    title: 'Resisting Urges and Cravings: Learn the DEADS Strategies',
    description:
      'Urges and cravings are natural. The DEADS strategies (Delay, Escape, Avoid, Distract and Substitute) can help manage and control urges',
    technical_description:
      "The DEADS strategies help clients manage intense urges or cravings, especially in the context of addictive behaviors. These strategies – Delay, Escape, Avoid, Distract and Substitute – are designed to diminish the power of urges, providing practical steps for clients to cope effectively.\n\nDEADS is instrumental in navigating the unpredictable nature of urges, fostering self-empowerment and independence in clients' recovery journey. However, specific research directly supporting the effectiveness of DEADS strategies in therapy is limited.",
    type: 'education',
    tags: ['Addiction'],
    blocks: [
      {
        id: 'deads-reading',
        type: 'reading',
        heading: '',
        content:
          '“Urge control” is a common term used in addiction treatment for managing waves of cravings as they come and go. Some urges are brief and related to specific triggers and on average a craving lasts 15 minutes or less. If an urge or craving lasts only 15 minutes the most logical approach is to delay the behavior when you first get the urge. The urge can and will go away. “Urge Surfing” allows you to observe the urge rise on a wave as you ride the wave all the way through until the urge is gone. Urges do not only apply to addiction – they also refer to mental habits and reactions. Two examples are the urge to react in the same way you have in the past or the urge to lash out in anger. The DEADS strategies provide a practical framework for managing urges and breaking habitual patterns.',
      },
      {
        id: 'delay',
        type: 'reading',
        heading: 'Delay',
        content:
          '**When you feel an urge avoid acting on it immediately.** Give yourself some time. Delay means putting off reacting using or giving in to the craving urge or intense emotion for a time while understanding the urge will go away. Even if you can delay for 5-10 minutes that may change the experience you have making you better able to handle it. Delaying allows the initial intensity of the urge to subside giving you a chance to think more rationally. It prevents impulsive actions and provides an opportunity for the rational mind to regain control.',
      },
      {
        id: 'escape',
        type: 'reading',
        heading: 'Escape',
        content:
          '**Physically remove yourself from the situation or environment triggering the urge.** For example leave the room if you are having a heated argument where you may lose control. Take a pause. Avoid situations that you know can be triggering. If you regularly go places that remind you of using drugs for example avoid those locations or situations. Escaping allows you to create distance between yourself and the source of the temptation. This physical separation can reduce the immediate impact of the urge and give you a chance to reassess your choices in a less stimulating environment.',
      },
      {
        id: 'avoid',
        type: 'reading',
        heading: 'Avoid',
        content:
          '**Identify and avoid situations, people or triggers that commonly lead to your urges.** Avoidance is a proactive strategy to minimize exposure to situations that may elicit undesired behaviors. By recognizing and sidestepping potential triggers you reduce the likelihood of giving in to your urges.',
      },
      {
        id: 'distract',
        type: 'reading',
        heading: 'Distract',
        content:
          '**Redirect your attention to something else engaging instead of the urge.** Distract means that you take control of the urge by getting busy and paying attention to something other than what is triggering you. Avoid sitting and ruminating on the urge – instead put your focus on something else. For some people that may be baking or creating art while for others it could be watching TV reading a book taking a walk showering or exercising. Distracting yourself diverts your mind from the urge and helps break the cycle of obsessive thoughts. Whether it is a hobby or any constructive activity the key is to shift your focus away from the urge until it loses its grip on you.',
      },
      {
        id: 'substitute',
        type: 'reading',
        heading: 'Substitute',
        content:
          '**Replace the undesired behavior with a healthier alternative.** If you have difficulty managing your anger take a walk. If you are trying to stop smoking pick up a piece of fruit or vegetable. Although a carrot or celery stick may not give you the same effect it helps you by simply having something to put in your mouth. You could also use a toothpick or straw. The trick is to come up with something that can be easily substituted in. Substitution involves finding a positive and constructive activity that fulfills the same need or desire as the urge. This way you address the root cause of the urge while channeling your energy into a more positive outlet.',
      },
      {
        id: 'examples',
        type: 'reading',
        heading: 'Examples',
        content:
          "### Overcoming Anger \n*John* often felt the urge to react angrily in stressful situations. By using the **Escape** strategy, he started stepping out for a brief walk when he felt anger rising. This physical separation helped him reassess the situation calmly, leading to more measured responses.\n\n### Breaking a Smoking Habit\n*Emma*, trying to quit smoking, adopted the **Substitute** strategy. Whenever she craved a cigarette, she'd chew on a carrot stick. This simple act of substitution provided a similar physical sensation without the harmful effects of smoking, aiding her journey to quit.",
      },
      {
        id: 'takeaways',
        type: 'text_input',
        heading: 'Applying these strategies',
        subheading:
          'What is one way you can apply these strategies for your urges?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.342Z',
    updated_at: '2024-03-20T22:45:29.342Z',
  },
  {
    id: 't-00000A',
    slug: 'resisting-urges-and-cravings-try-using-deads',
    title: 'Resisting Urges and Cravings: Try using DEADS',
    description:
      'This exercise helps you write down a plan for using the DEADS strategies to overcome urges and cravings.',
    technical_description: '',
    type: 'exercise',
    tags: ['Addiction'],
    blocks: [
      {
        id: 'specific-urge',
        type: 'text_input',
        heading:
          'What specific urge or behavior are you experiencing or planning for?',
        subheading: '',
      },
      {
        id: 'intensity-rate',
        type: 'scale',
        heading: 'Rate the intensity of the urge on a scale of 1 to 10',
        min: 1,
        max: 10,
        min_label: 'Extremely mild',
        max_label: 'Extremely intense',
        mid_label: '',
      },
      {
        id: 'reasons',
        type: 'text_input',
        heading:
          'What are three reasons why controling this urge might be beneficial?',
        subheading: '',
      },
      {
        id: 'choosing-a-strategy',
        type: 'text_input',
        heading: 'What is one strategy for controling your urge?',
        subheading:
          "Remember, DEADS stands for Delay, Escape, Avoid, Distract and Substitute, and you can use any of these! Be specific about how long you will delay, where you might escape to, what you'll use to substitute, and so on.",
      },
      {
        id: 'obstacles',
        type: 'text_input',
        heading: 'Potential Obstacles',
        subheading:
          'What obstacles might get in the way of your strategy for controling this urge? Be specific.',
      },
      {
        id: 'overcome',
        type: 'text_input',
        heading: 'Plan for overcoming obstacles',
        subheading:
          'What is one way you can add to your plan to overcome these obstacles?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.342Z',
    updated_at: '2024-03-20T22:45:29.342Z',
  },
  {
    id: 't-00000B',
    slug: 'sleep-diary-entry',
    title: 'Sleep Diary Entry',
    description:
      'It’s hard to remember how we slept a week ago. Sleep diaries give us objective data to work with. As you fill this out, dont worry about being perfectly accurate, just do your best.',
    technical_description: '',
    type: 'tracker',
    tags: ['Sleep & Insomnia'],
    blocks: [
      {
        id: 'sleep-rating',
        type: 'scale',
        heading: 'How do you feel about your sleep overall?',
        min: 1,
        max: 10,
        min_label: 'Extremely bad',
        max_label: 'Extremely good',
        mid_label: '',
      },
      {
        id: 'hours-napping',
        type: 'number',
        heading: 'How many hours did you nap yesterday?',
        min: 0,
        max: 24,
      },
      {
        id: 'hours-in-bed-before-sleep',
        type: 'number',
        heading: 'How many hours did you spend in bed before sleeping?',
        min: 0,
        max: 24,
      },
      {
        id: 'hours-asleep',
        type: 'number',
        heading: 'How many hours did you spend asleep?',
        min: 0,
        max: 24,
      },
      {
        id: 'hours-awake-at-night',
        type: 'number',
        heading:
          'How many hours were you awake after waking up during the night?',
        min: 0,
        max: 24,
      },
      {
        id: 'time-got-into-bed',
        type: 'time',
        heading: 'What time did you get into bed?',
      },
      {
        id: 'time-turned-off-lights',
        type: 'time',
        heading: 'What time did you turn off the lights?',
      },
      {
        id: 'time-fell-asleep',
        type: 'time',
        heading: 'What time did you fall asleep?',
      },
      {
        id: 'time-woke-up',
        type: 'time',
        heading: 'What time did you wake up this morning?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.343Z',
    updated_at: '2024-03-20T22:45:29.343Z',
  },
  {
    id: 't-00000C',
    slug: 'sleep-hygiene-tips',
    title: 'Sleep Hygiene Tips',
    description:
      'This sheet outlines some helpful tips to get a better night of sleep based on research about the human body and our behaviors.',
    technical_description:
      "Therapists should consider introducing sleep hygiene to clients when sleep-related issues are identified, particularly if these issues are impacting the client's physical health, mental health, or overall well-being. Sleep hygiene refers to the practices, habits, and environmental factors that are conducive to sleeping well on a regular basis. It is especially pertinent in cases where sleep disturbances may be exacerbating or contributing to mental health disorders, such as depression or anxiety.",
    type: 'education',
    tags: ['Sleep & Insomnia'],
    blocks: [
      {
        id: 'sleep-as-needed',
        type: 'reading',
        heading: 'Sleep only as needed',
        content:
          "Only sleep as much as you need to feel rested. Sleeping in excess might disrupt your sleeping pattern. Most people need to sleep 7-9 hours a day, and any more can jeopardize the next day's sleep.",
      },
      {
        id: 'consistent-schedule',
        type: 'reading',
        heading: 'Maintain a Consistent Sleep Schedule',
        content:
          "Try to go to bed and wake up at the same time every day, even on weekends. This regularity reinforces your body's sleep-wake cycle.",
      },
      {
        id: 'create-restful-environment',
        type: 'reading',
        heading: 'Create a Restful Environment',
        content:
          'Keep your bedroom quiet, dark, and cool. Consider using room-darkening shades, earplugs, a fan, or other devices to create an environment that suits your needs.',
      },
      {
        id: 'limit-naps',
        type: 'reading',
        heading: 'Limit Daytime Naps',
        content:
          'Long daytime naps can interfere with nighttime sleep. If you choose to nap, limit yourself to about 10 to 30 minutes and make it during the mid-afternoon.',
      },
      {
        id: 'physical-activity',
        type: 'reading',
        heading: 'Include Physical Activity in Your Daily Routine',
        content:
          'Regular physical activity can promote better sleep, but try to avoid vigorous exercise within an hour of bedtime.',
      },
      {
        id: 'caffeine',
        type: 'reading',
        heading: 'Limit Caffeine After Morning',
        content:
          'Caffeine stays in our system for an extended period of time, so it is best to stop drinking caffeine in the mid-afternoon to avoid sleep disruptions.',
      },
      {
        id: 'manage-worries',
        type: 'reading',
        heading: 'Manage Worries',
        content:
          "Try to resolve your worries or concerns before bedtime. Jot down what's on your mind and then set it aside for tomorrow.",
      },
      {
        id: 'eat-regular-meals',
        type: 'reading',
        heading: 'Eat Regular Meals',
        content:
          'Try to eat meals at regular times each day. Avoid heavy or large meals within a couple of hours of bedtime. A light snack is okay.',
      },
      {
        id: 'avoid-alcohol',
        type: 'reading',
        heading: 'Avoid Alcohol',
        content:
          'Avoid consuming alcohol before bed, as it can interfere with your sleep cycle. While it might make you fall asleep faster, it often leads to disruptions in sleep later in the night.',
      },
      {
        id: 'avoid-smoking',
        type: 'reading',
        heading: 'Avoid Smoking',
        content:
          'Nicotine is a stimulant and can make it difficult to fall asleep. Avoid smoking or using tobacco products close to bedtime or if you wake up in the middle of the night.',
      },
      {
        id: 'takeaways',
        type: 'text_input',
        heading: 'Your plan to get better sleep',
        subheading: 'What are two changes you can make to have better sleep?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.344Z',
    updated_at: '2024-03-20T22:45:29.344Z',
  },
  {
    id: 't-00000D',
    slug: 'core-beliefs',
    title: 'Core Beliefs',
    description:
      'Learn how core beliefs affect thoughts, behaviors, and emotions',
    technical_description: '',
    type: 'education',
    tags: ['CBT'],
    blocks: [
      {
        id: 'core-beliefs',
        type: 'reading',
        heading: 'What are core beliefs?',
        content:
          '**Core beliefs** are a person’s most deeply held ideas about themselves, others, and the world. These beliefs can form during childhood or through traumatic events and then then shape our perspectives on many situations and life experience going forward. These beliefs can be so strong that two individuals with different core beliefs might have completely different thoughts, behaviors, or emotions about the very same situation. \n\n Importantly, core beliefs are ideas that we have learned rather than facts, and can be changed over time through carefuly analysis of our thoughts.',
      },
      {
        id: 'bad-grade-example',
        type: 'reading',
        heading:
          'People with different core beliefs might react to the same situation differently',
        content:
          '## Situation: no response to a message sent to a new friend for 24 hours\n## Person A\nCore belief: "I am not fun to be around"\n* Thought: they must be angry or upset with me\n* Feeling: Sadness and shame\n* Behavior: Avoids the friend\n\n## Person B\nCore belief: "I am a unique person who is fun to be around"\n* Thought: I wonder why they haven\'t responded, maybe they got busy\n* Feeling: Anticipating\n* Behavior: Plans to follow up if there\'s no response in another few hours',
      },
      {
        id: 'harmful-core-beliefs',
        type: 'reading',
        heading: 'Common harmful core beliefs',
        content:
          '\n- **Helpless**: "I am weak", "I am a loser", "I am trapped"\n- **Unlovable**: "I am unlovable", "I will end up alone", "No one likes me"\n- **Worthless**: "I am bad", "I don’t deserve to live", "I am worthless"\n- **External Danger**: "The world is dangerous", "People can’t be trusted", "Nothing ever goes right"',
      },
      {
        id: 'consequences-of-harmful-core-beliefs',
        type: 'reading',
        heading: 'Benefits of addressing harmful core beliefs',
        content:
          "1. **Enhancement of Mental Well-being**: Harmful core beliefs can be a root cause of various mental health issues, such as anxiety, depression, and low self-esteem. By addressing these beliefs, individuals can experience significant improvements in their mental well-being and overall quality of life.\n2. **Improved Interpersonal Relationships**: These beliefs often lead to difficulties in forming and maintaining healthy relationships. Individuals might struggle with trust, communication, and intimacy due to negative perceptions of themselves or others. Changing these beliefs can lead to healthier, more fulfilling relationships.\n3. **Positive Behavioral Changes**: Harmful core beliefs can lead to destructive or self-defeating behaviors. By identifying and modifying these beliefs, individuals can adopt healthier behaviors, leading to more successful and satisfying lives.\n4. **Enhanced Coping Skills**: These beliefs often limit an individual's ability to cope effectively with stress and challenges. Addressing them can lead to the development of more adaptive coping mechanisms, enhancing resilience in the face of life's difficulties.\n5. **Greater Self-Awareness**: Exploring and challenging harmful core beliefs fosters greater self-awareness and understanding. This self-reflection can be empowering, enabling individuals to take control of their thoughts and emotions.\n6. **Reduction in Cognitive Distortions**: Harmful core beliefs are often intertwined with cognitive distortions—ways of thinking that are not based on reality. Addressing these beliefs helps in reducing these distortions, leading to more rational, balanced thinking.\n7. **Long-Term Benefits**: While the process of changing core beliefs can be challenging, the long-term benefits are substantial. Not only do individuals experience immediate improvements in their day-to-day life, but they also lay the groundwork for enduring mental health and well-being.\n8. **Contribution to Overall Health**: Mental health significantly impacts physical health. By addressing harmful core beliefs, individuals can reduce stress and anxiety levels, which in turn can have positive effects on physical health, including reduced risk of chronic diseases.",
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.344Z',
    updated_at: '2024-03-20T22:45:29.344Z',
  },
  {
    id: 't-00000E',
    slug: 'practice-decatastrophizing',
    title: 'Practice Decatastrophizing',
    description:
      'The Decatastrophizing activity is a tool to help you challenge and gain perspective on worries that may be exaggerated or unlikely. It guides you through analyzing the likelihood and impact of your worries.',
    technical_description: '',
    type: 'exercise',
    tags: ['CBT', 'Anxiety'],
    blocks: [
      {
        id: 'decatastrophizing-info',
        type: 'reading',
        heading: 'How cognitive distortions can lead to catastrophizing',
        content:
          '**Cognitive distortions** are irrational thoughts that have the power to influence how you feel. Everyone has some cognitive distortions—they’re a normal part of being human. However, when cognitive distortions are too plentiful or extreme, they can be harmful.\n\nOne common type of cognitive distortion is called **catastrophizing**. When catastrophizing, the importance of a problem is exaggerated, or the worst possible outcome is assumed to be true. By learning to question your own thoughts, you can correct many of these cognitive distortions.',
      },
      {
        id: 'worry-description',
        type: 'text_input',
        heading: 'What are you worried about?',
        subheading: 'Describe in detail what you are currently worried about.',
      },
      {
        id: 'worry-likelihood',
        type: 'text_input',
        heading: 'How likely is it that your worry will come true?',
        subheading:
          'Give examples of past experiences or other evidence to support your answer.',
      },
      {
        id: 'worst-outcome',
        type: 'text_input',
        heading:
          "If your worry does come true, what's the worst that could happen?",
        subheading:
          'Describe the worst possible outcome if your worry comes true.',
      },
      {
        id: 'likely-outcome',
        type: 'text_input',
        heading: "If your worry does come true, what's most likely to happen?",
        subheading:
          'Describe what is most likely to happen if your worry comes true.',
      },
      {
        id: 'outcome-probability',
        type: 'group',
        heading:
          "If your worry comes true, what are the chances you'll be okay...",
        blocks: [
          {
            id: 'one-week',
            type: 'scale',
            heading: 'In one week',
            min: 0,
            max: 10,
            min_label: 'Nearly impossible',
            max_label: 'Almost certain',
            mid_label: '50/50 chance',
          },
          {
            id: 'one-month',
            type: 'scale',
            heading: 'In one month',
            min: 0,
            max: 10,
            min_label: 'Nearly impossible',
            max_label: 'Almost certain',
            mid_label: '50/50 chance',
          },
          {
            id: 'one-year',
            type: 'scale',
            heading: 'In one year',
            min: 0,
            max: 10,
            min_label: 'Nearly impossible',
            max_label: 'Almost certain',
            mid_label: '50/50 chance',
          },
        ],
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.344Z',
    updated_at: '2024-03-20T22:45:29.344Z',
  },
  {
    id: 't-00000F',
    slug: 'clarifying-your-values',
    title: 'Clarifying Your Values',
    description:
      'Values are like a compass that guides us through life. They represent what is most important to us and influence our decisions, behaviors, and satisfaction with our lives. When facing challenges, aligning our actions with our core values can provide direction and meaning.',
    technical_description: '',
    type: 'exercise',
    tags: ['Journaling', 'ACT'],
    blocks: [
      {
        id: 'identify-values',
        type: 'text_input',
        heading: 'List your core values',
        subheading:
          'Consider what truly matters to you in different areas of your life, such as relationships, career, personal growth, health, and leisure. What principles or qualities do you hold dear? (e.g., honesty, compassion, achievement, independence)',
      },
      {
        id: 'describe-challenge',
        type: 'text_input',
        heading: 'Describe your current challenge',
        subheading:
          "Briefly describe a challenge you're currently experiencing in your life.",
      },
      {
        id: 'values-for-challenge',
        type: 'text_input',
        heading: 'Values related to the challenge',
        subheading:
          'Which values from your list are related to this challenge?',
      },
      {
        id: 'belief-in-values',
        type: 'text_input',
        heading: 'How strongly do you believe in these values?',
        subheading:
          'Rate your belief in each of the values from 1-10 and jot down why you feel this way. ',
      },
      {
        id: 'actions',
        type: 'text_input',
        heading: 'Action steps aligned with values',
        subheading:
          'Based on your values and their ratings, think of specific actions you can take to better align with these values in addressing your challenge.',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.345Z',
    updated_at: '2024-03-20T22:45:29.345Z',
  },
  {
    id: 't-00000G',
    slug: 'design-a-mindful-ritual',
    title: 'Design a Mindful Ritual',
    description:
      'Rituals create a safe space for us to recognize and honor significant events and transitions in our lives. We allow ourselves to safely and comfortably experience and validate the emotions connected to the event or change.',
    technical_description: '',
    type: 'exercise',
    tags: ['Positive behavior', 'Mindfulness'],
    blocks: [
      {
        id: 'why-rituals',
        type: 'reading',
        heading: 'Any activity can become a mindful ritual',
        content:
          "A mindful ritual is a regular practice or routine performed with intention and awareness, designed to bring you into the present moment. Unlike mindless habits, mindful rituals are about fully engaging with an activity, using it as a tool to ground yourself in the 'now'. Any activity can become a mindful experience by applying some of these techniques:\n\n- Focusing on your senses during the activity\n- Acknowledging and letting go of intrusive thoughts and being in the moment\n- Appreciating the details and steps involved in the activity, even if they are small\n  \nThe goal of a mindful ritual is to create a space where you are fully attentive and immersed, which can lead to increased relaxation, joy, and mindfulness in everyday life.",
      },
      {
        id: 'brainstorm-ritual',
        type: 'text_input',
        heading: 'Design your ritual: identify pleasurable activities',
        subheading:
          'Think of activities that you find enjoyable or relaxing. What are the things that make you feel calm, happy, or at peace? These could be as simple as making a cup of tea, reading, gardening, or listening to music.',
      },
      {
        id: 'detail-ritual',
        type: 'text_input',
        heading: 'Make a plan for your ritual',
        subheading:
          'Choose one activity from your list and imagine turning it into a ritual. Describe the steps involved in this activity. How can you perform these steps more mindfully? For example, if your activity is making tea, consider the process of selecting the tea, boiling the water, pouring it, and savoring the aroma and taste.',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.345Z',
    updated_at: '2024-03-20T22:45:29.345Z',
  },
  {
    id: 't-00000H',
    slug: 'run-a-behavioral-experiment',
    title: 'Run a Behavioral Experiment',
    description:
      "This activity is part of cognitive behavioral therapy, teaching how to test thoughts and beliefs through experiments. It's an action-based method to learn how to replace unhelpful thoughts with more helpful ones.",
    technical_description:
      "Behavioral experiments are a crucial aspect of cognitive therapy, providing a dynamic and practical approach to challenging and modifying clients' unhelpful beliefs and assumptions. When integrating behavioral experiments into therapy, therapists should consider the following guidelines: \n\n1. **Clear Rationale and Hypothesis Testing**: The experiment should have a clear rationale that is easily understood by the client. The client should be able to articulate what they aim to learn, discover, or disprove through the experiment. This ensures that the experiment aligns with the client’s specific needs and therapy goals.\n2. **Meaningful and Collaborative Design**: The design of the experiment should be meaningful to the client and developed collaboratively. The client should choose whether and how to engage in the experiment. It's essential that the experiment resonates with the client's values to maintain motivation and willingness.\n3. **Addressing Fears and Building Confidence**: The experiment should aim to confront fears and unhelpful cognitions while also building the client’s confidence. Clients should feel reasonably capable of undertaking the task with a self-efficacy rating of 70% or more. Early successes in lower-stakes experiments can build confidence for more challenging tasks.\n4. **Structured Implementation and Monitoring**: Writing out a detailed plan, noting baseline functioning, and monitoring throughout the experiment are crucial. This includes documenting fearful predictions, assumptions, current responses, and any safety behaviors or coping strategies that could affect the experiment. Post-experiment, it's important to record what actually happened.\n5. **Incorporation of Findings**: Work with the client to understand the outcomes of the experiment. Discuss what conclusions can be drawn and how these insights apply to everyday situations. This involves planning subsequent experiments based on the newfound understanding.\n\nTherapists should use behavioral experiments judiciously, ensuring that each experiment is tailored to the client’s specific challenges and therapy goals. These experiments should be integrated into a broader treatment plan, carefully monitored, and adapted based on the client’s feedback and progress.",
    type: 'exercise',
    tags: ['CBT', 'Positive behavior'],
    blocks: [
      {
        id: 'intro-to-behavioral-experiment',
        type: 'reading',
        heading: 'Introduction to Behavioral Experiment',
        content:
          'Cognitive behavioral therapy teaches us many ways to discover how our thoughts, feelings, actions, and external events affect each other in positive and negative ways. A behavioral experiment is a way to put a thought or belief to the test. This can be helpful when you have a thought or belief that you know isn’t helpful but is hard to get rid of. Think outside the box and look at the thought like a scientist would, trying to prove it as true or false. This is an action-based way to learn how to test your thoughts and replace them with ideas that are more helpful.',
      },
      {
        id: 'thought-belief-tested',
        type: 'text_input',
        heading: 'Thought or Belief Being Tested',
        subheading: 'Identify the thought or belief you want to test.',
      },
      {
        id: 'experiment',
        type: 'text_input',
        heading: 'Behavior change experiment',
        subheading:
          "Plan an experiment where you'll change your behavior for a period of time, being as specific as possible. List as many small steps as needed. What will you do? How? When? Who will you do this with?",
      },
      {
        id: 'prediction',
        type: 'text_input',
        heading: 'Prediction',
        subheading: 'What do you think will happen?',
      },
      {
        id: 'certainty-outcome',
        type: 'scale',
        heading: 'Certainty of Outcome',
        min: 1,
        max: 10,
        min_label: 'Very unsure',
        max_label: 'Very sure',
        mid_label: '',
      },
      {
        id: 'outcome',
        type: 'text_input',
        heading: 'Outcome',
        subheading: 'What happened during the experiment?',
      },
      {
        id: 'experiment-experience',
        type: 'text_input',
        heading: 'Experiment Experience',
        subheading: 'What was completing the experiment like for you?',
      },
      {
        id: 'learnings',
        type: 'text_input',
        heading: 'Learnings',
        subheading: 'What did you learn?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.345Z',
    updated_at: '2024-03-20T22:45:29.345Z',
  },
  {
    id: 't-00000I',
    slug: 'emotional-regulation-skills',
    title: 'Emotional Regulation Skills',
    description:
      'This activity helps us understand and cope with emotions using a simple structured thinking process.',
    technical_description:
      'Emotional regulation skills are crucial in cognitive behavioral therapy, aiding clients in managing intense emotions, reducing impulsivity, and enhancing self-awareness. Activities focus on identifying emotional triggers, understanding the physiological and cognitive aspects of emotions, and practicing adaptive coping strategies. The effectiveness of emotional regulation strategies is well-documented in reducing symptoms of various psychological disorders (Gross, 2014; Aldao, Nolen-Hoeksema, & Schweizer, 2010). These skills are integral in fostering emotional resilience and psychological well-being.\n### References:\n1. Gross, J. J. (2014). *The emerging field of emotion regulation: An integrative review*. Review of General Psychology, 8(3), 271-299.\n2. Aldao, A., Nolen-Hoeksema, S., & Schweizer, S. (2010). *Emotion-regulation strategies across psychopathology: A meta-analytic review*. Clinical Psychology Review, 30(2), 217-237.',
    type: 'exercise',
    tags: ['CBT', 'Managing Emotions'],
    blocks: [
      {
        id: 'intro-to-emotional-regulation',
        type: 'reading',
        heading: 'How emotions manifest',
        content:
          '### Emotions: The What and Why\nEmotions are complex reactions that involve our bodies, minds, and behaviors. They can be triggered by thoughts, memories, external events, or even by other emotions. Understanding your emotions involves recognizing not just what you feel, but also why you feel it. This awareness is the first step in learning how to regulate and manage your emotions effectively.\n\n### Triggers and Emotional Responses\nTriggers are specific stimuli that elicit an emotional response. These can be external, like a conversation or an event, or internal, like a memory or thought. Recognizing your triggers is crucial as they often set off a chain reaction, leading to emotional responses that might be disproportionate or unhelpful. By identifying and understanding these triggers, you can begin to anticipate and prepare for emotional reactions, or even start to change your response patterns.',
      },
      {
        id: 'reflect-on-emotions',
        type: 'text_input',
        heading: 'Identify and understand your emotions',
        subheading:
          'Write them down and describe any situations that caused strong emotional reactions this week. Try to identify what you were feeling and why. Think about the physical sensations you experienced with each emotion. Did your heart rate increase? Did you feel tension somewhere in your body?',
      },
      {
        id: 'emotional-triggers',
        type: 'text_input',
        heading: 'Recognizing emotional triggers',
        subheading:
          'List the events, interactions, or thoughts that seem to trigger these emotions. Try to write down why these moments trigger your emotions.',
      },
      {
        id: 'learnings',
        type: 'text_input',
        heading: 'Takeaways',
        subheading: 'What do you want to take away from this reflection?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.345Z',
    updated_at: '2024-03-20T22:45:29.345Z',
  },
  {
    id: 't-00000J',
    slug: 'simple-breathing-exercise',
    title: 'Simple breathing exercise',
    description:
      'Slowing down and focusing on breathing is one of the simplest and most effective things you can do to manage intense emotions or situations.',
    technical_description:
      'Relaxation techniques such as deep breathing have significant empirical support in reducing stress and anxiety symptoms, particularly when completed frequently. They can be incorporated in several treatment modalities.\n\n### References:\n- Toussaint, L., Nguyen, Q. A., Roettger, C., Dixon, K., Offenbächer, M., Kohls, N., ... & Sirois, F. (2021). Effectiveness of progressive muscle relaxation, deep breathing, and guided imagery in promoting psychological and physiological states of relaxation. Evidence-Based Complementary and Alternative Medicine, 2021.',
    type: 'exercise',
    tags: ['Mindfulness'],
    blocks: [
      {
        id: 'mood-before',
        type: 'scale',
        heading: 'How are you feeling before starting this exercise?',
        min: 1,
        max: 10,
        min_label: 'Very bad',
        max_label: 'Very good',
        mid_label: '',
      },
      {
        id: 'breathing-exercise',
        type: 'video',
        heading: 'Simple, 5 minute breathing exercise',
        url: 'https://www.youtube.com/embed/inpok4MKVLM?si=jEc07woURYGgelRx',
      },
      {
        id: 'mood-after',
        type: 'scale',
        heading: 'How are you feeling after completing the exercise?',
        min: 1,
        max: 10,
        min_label: 'Very bad',
        max_label: 'Very good',
        mid_label: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.346Z',
    updated_at: '2024-03-20T22:45:29.346Z',
  },
  {
    id: 't-00000L',
    slug: 'weekly-plan-for-positive-behaviors',
    title: 'Weekly Plan for Positive Behaviors',
    description:
      "This planner helps you engage in the activities you find meaningful or enjoyable by planning out your week in advance. As you plan your week, focusing on small, achievable tasks that align with your values and interests and build up over time. Don't worry about planning out every single moment of your week.",
    technical_description:
      "Behavioral Activation (BA) is a cognitive-behavioral therapy technique designed to treat depression by increasing the patient's contact with positively reinforcing activities. BA operates under the principle that depression is maintained by a cycle of negative mood, reduced activity, and avoidance of life's challenges, which further diminishes opportunities for positive reinforcement. This worksheet is a tool for facilitating BA by helping patients to systematically track and schedule activities that are aligned with their values and that can provide a sense of achievement or pleasure.\n\n### References:\n -Cuijpers, P., Van Straten, A., & Warmerdam, L. (2007). Behavioral activation treatments of depression: A meta-analysis. Clinical psychology review, 27(3), 318-326.",
    type: 'exercise',
    tags: ['Behavioral Activation'],
    blocks: [
      {
        id: 'monday',
        type: 'group',
        heading: 'Monday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
      {
        id: 'tuesday',
        type: 'group',
        heading: 'Tuesday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
      {
        id: 'wednesday',
        type: 'group',
        heading: 'Wednesday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
      {
        id: 'thursday',
        type: 'group',
        heading: 'Thursday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
      {
        id: 'friday',
        type: 'group',
        heading: 'Friday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
      {
        id: 'saturday',
        type: 'group',
        heading: 'Saturday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
      {
        id: 'sunday',
        type: 'group',
        heading: 'Sunday',
        blocks: [
          {
            id: 'morning',
            type: 'text_input',
            heading: '',
            subheading: 'Morning',
          },
          {
            id: 'afternoon',
            type: 'text_input',
            heading: '',
            subheading: 'Afternoon',
          },
          {
            id: 'evening',
            type: 'text_input',
            heading: '',
            subheading: 'Evening',
          },
        ],
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.346Z',
    updated_at: '2024-03-20T22:45:29.346Z',
  },
  {
    id: 't-00000M',
    slug: 'progressive-muscle-relaxation',
    title: 'Progressive Muscle Relaxation',
    description:
      'This 5-minute exercise lowers stress and anxiety through systematic tensing and relaxing of the muscles in the body. The exercise can provide immediate relaxation, and is most effective with frequent practice. When tensing muscles, do not tense them to the point of strain. Skip any parts of the body that are currently injured.',
    technical_description:
      'Relaxation techniques such as Progressive Muscle Relaxation have significant empirical support in reducing stress and anxiety symptoms, particularly when completed frequently. They can be incorporated in several treatment modalities.\n\n### References:\n- Toussaint, L., Nguyen, Q. A., Roettger, C., Dixon, K., Offenbächer, M., Kohls, N., ... & Sirois, F. (2021). Effectiveness of progressive muscle relaxation, deep breathing, and guided imagery in promoting psychological and physiological states of relaxation. Evidence-Based Complementary and Alternative Medicine, 2021.',
    type: 'exercise',
    tags: ['Mindfulness'],
    blocks: [
      {
        id: 'relaxation-before',
        type: 'scale',
        heading: 'Rate your level of relaxation before you start',
        min: 1,
        max: 10,
        min_label: 'Very relaxed',
        max_label: 'Very tense',
        mid_label: '',
      },
      {
        id: 'relaxation-video',
        type: 'video',
        heading: 'Follow this short video to help you relax',
        url: 'https://www.youtube.com/embed/1nZEdqcGVzo?si=swZPETcToR5loRU3',
      },
      {
        id: 'relaxation-after',
        type: 'scale',
        heading: 'Rate your level of relaxation after completing the exercise',
        min: 1,
        max: 10,
        min_label: 'Very relaxed',
        max_label: 'Very tense',
        mid_label: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.347Z',
    updated_at: '2024-03-20T22:45:29.347Z',
  },
  {
    id: 't-00000N',
    slug: 'guided-imagery-exercise',
    title: 'Guided Imagery Exercise',
    description:
      'This short mindfulness exercise can help reduce stress. Practice this exercise frequently to see increased benefits.',
    technical_description:
      'Relaxation techniques such as guided imagery have significant empirical support in reducing stress and anxiety symptoms, particularly when completed frequently. They can be incorporated in several treatment modalities.\n\n### References:\n- Toussaint, L., Nguyen, Q. A., Roettger, C., Dixon, K., Offenbächer, M., Kohls, N., ... & Sirois, F. (2021). Effectiveness of progressive muscle relaxation, deep breathing, and guided imagery in promoting psychological and physiological states of relaxation. Evidence-Based Complementary and Alternative Medicine, 2021.\n- Patricolo, G. E., LaVoie, A., Slavin, B., Richards, N. L., Jagow, D., & Armstrong, K. (2017). Beneficial effects of guided imagery or clinical massage on the status of patients in a progressive care unit. Critical care nurse, 37(1), 62-69.',
    type: 'exercise',
    tags: ['Mindfulness'],
    blocks: [
      {
        id: 'relaxation-before',
        type: 'scale',
        heading: 'Rate your level of relaxation before you start',
        min: 1,
        max: 10,
        min_label: 'Very relaxed',
        max_label: 'Very tense',
        mid_label: '',
      },
      {
        id: 'imagery-video',
        type: 'video',
        heading: 'Follow the calming images in this vodeo help you relax',
        url: 'https://www.youtube.com/embed/6am3OS-Ejzk?si=T5b0aT-BvGltBtvF',
      },
      {
        id: 'relaxation-after',
        type: 'scale',
        heading: 'Rate your level of relaxation after completing the exercise',
        min: 1,
        max: 10,
        min_label: 'Very relaxed',
        max_label: 'Very tense',
        mid_label: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.347Z',
    updated_at: '2024-03-20T22:45:29.347Z',
  },
  {
    id: 't-00000O',
    slug: 'mood-tracker',
    title: 'Mood Tracker',
    description:
      'Keeping track of your mood can help you develop self-awareness and learn more about what contributes to how you feel.',
    technical_description:
      'Research suggests that using mood tracking can increase self-awareness and help people reflect on a past mood to determine what was happening and why they might have been experiencing that emotion or mood.\n\n### References:\n - Schueller, S. M., Neary, M., Lai, J., & Epstein, D. A. (2021). Understanding people’s use of and perspectives on mood-tracking apps: interview study. JMIR mental health, 8(8), e29368.',
    type: 'tracker',
    tags: [],
    blocks: [
      {
        id: 'mood',
        type: 'scale',
        heading: 'How are you feeling?',
        min: 1,
        max: 10,
        min_label: 'Very bad',
        max_label: 'Very good',
        mid_label: '',
      },
      {
        id: 'notes',
        type: 'text_input',
        heading: 'What could be contributing to how you are feeling?',
        subheading: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.347Z',
    updated_at: '2024-03-20T22:45:29.347Z',
  },
  {
    id: 't-00000P',
    slug: 'dbt-diary-card-individual',
    title: 'DBT Diary Card: Individual',
    description:
      'Use this diary card to track your progress with your therapist.',
    technical_description: '',
    type: 'tracker',
    tags: ['DBT'],
    blocks: [
      {
        id: 'urges',
        type: 'group',
        heading: 'How strongly did you experience each of the urges?',
        blocks: [
          {
            id: 'suicide',
            type: 'scale',
            heading: 'Suicide',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'self-harm',
            type: 'scale',
            heading: 'Self Harm',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'consume-substances',
            type: 'scale',
            heading: 'Consume substances',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'quit-therapy',
            type: 'scale',
            heading: 'Quit Therapy',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
        ],
      },
      {
        id: 'emotions',
        type: 'group',
        heading:
          'How strongly did you experience each of the following emotions?',
        blocks: [
          {
            id: 'emotional-misery',
            type: 'scale',
            heading: 'Emotional Misery',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'physical-misery',
            type: 'scale',
            heading: 'Physical Misery',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'joy',
            type: 'scale',
            heading: 'Joy',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
        ],
      },
      {
        id: 'beliefs',
        type: 'group',
        heading: 'Belief I can change or regulate my:',
        blocks: [
          {
            id: 'emotions',
            type: 'scale',
            heading: 'Emotions',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'actions',
            type: 'scale',
            heading: 'Actions',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
          {
            id: 'thoughts',
            type: 'scale',
            heading: 'Thoughts',
            min: 0,
            max: 5,
            min_label: 'Lowest',
            max_label: 'Highest',
            mid_label: '',
          },
        ],
      },
      {
        id: 'drugs-alcohol',
        type: 'multi_entry',
        heading: 'Drugs or alcohol consumed',
        blocks: [
          [
            {
              id: 'drug-type',
              type: 'short_text_input',
              heading: 'Type of Drug or Alcohol',
              subheading: '',
            },
            {
              id: 'amount',
              type: 'number',
              heading: 'Amount of Drinks or Drugs',
              min: 0,
              max: 100,
            },
          ],
        ],
      },
      {
        id: 'prescription-otc-drugs',
        type: 'multi_entry',
        heading: 'Prescription and over the counter drugs',
        blocks: [
          [
            {
              id: 'drug-type',
              type: 'short_text_input',
              heading: 'Type of Drug',
              subheading: '',
            },
            {
              id: 'amount',
              type: 'number',
              heading: 'Amount of Drinks or Drugs',
              min: 0,
              max: 100,
            },
          ],
        ],
      },
      {
        id: 'prescriptions',
        type: 'select',
        heading: 'Did you take prescription meds as instructed?',
        options: ['Yes', 'No'],
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.348Z',
    updated_at: '2024-03-20T22:45:29.348Z',
  },
  {
    id: 't-00000Q',
    slug: 'dbt-diary-card-skills',
    title: 'DBT Diary Card: Skills',
    description:
      'Use this diary card to track your progress for DBT skills work.',
    technical_description: '',
    type: 'tracker',
    tags: ['DBT'],
    blocks: [
      {
        id: 'skills-group',
        type: 'multi_entry',
        heading:
          'Which DBT skills did you use or try to use, and how did it go?',
        blocks: [
          [
            {
              id: 'skills',
              type: 'select',
              heading: 'Skill',
              options: {
                Mindfulness: [
                  'Wise mind',
                  'Observe (urge surfing)',
                  'Describe (put words on)',
                  'Participate (enter into experience)',
                  'Nonjudgemental stance',
                  'Being in the moment',
                  'Focus on what works',
                ],
                'Interpersonal Effectiveness': [
                  'Objectiveness: DEAR MAN',
                  'Relationships: GIVE',
                  'Self-respect: FAST',
                  'Validation',
                  'Strategies to change behavior',
                ],
                'Emotion Regulation': [
                  'Checked the facts',
                  'Opposite-to-emotion action',
                  'Mindful of current emotions',
                  'Accumulate positive experiences',
                  'Build MASTERY',
                  'Cope aheadReduce vulnerability: PLEASE',
                ],
                'Distress Tolerance': [
                  'Crisis: STOP skill',
                  'Crisis: Pros and cons',
                  'Distract (Adaptive Denial)',
                  'Self-soothe',
                  'Improve the moment',
                  'Radical Acceptance',
                ],
              },
            },
            {
              id: 'skill-rating',
              type: 'select',
              heading: 'How did it go?',
              options: [
                'Not used or thought about',
                "Didn't want to use",
                "Wanted to use but didn't",
                "Tried to use but couldn't",
                "Tried using but wasn't helpful",
                'Tried using and was helpful',
                "Used automatically but wasn't helpful",
                'Used automatically and was helpful',
              ],
            },
          ],
        ],
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.348Z',
    updated_at: '2024-03-20T22:45:29.348Z',
  },
  {
    id: 't-00000R',
    slug: 'exposure-tracker',
    title: 'Exposure Tracker',
    description:
      'Exposure therapy can help us face our fears in a gradual and systematic way, something that has been shown to work in clinical studies. This activity helps track progress and anxiety levels as you work through exposure exercises.',
    technical_description:
      'Research suggests that using mood tracking can increase self-awareness and help people reflect on a past mood to determine what was happening and why they might have been experiencing that emotion or mood.\n\n### References:\n - Schueller, S. M., Neary, M., Lai, J., & Epstein, D. A. (2021). Understanding people’s use of and perspectives on mood-tracking apps: interview study. JMIR mental health, 8(8), e29368.',
    type: 'tracker',
    tags: ['CBT'],
    blocks: [
      {
        id: 'exposure-activity',
        type: 'text_input',
        heading: 'Exposure activity',
        subheading: '',
      },
      {
        id: 'behavior-to-avoid',
        type: 'text_input',
        heading: 'What escape or safety behaviors do you want to avoid?',
        subheading: '',
      },
      {
        id: 'datetime',
        type: 'datetime',
        heading: 'Date and time of exposure',
      },
      {
        id: 'suds-start',
        type: 'scale',
        heading: 'Anxiety at start of session',
        min: 0,
        max: 10,
        min_label: 'No anxiety at all',
        max_label: 'Worst anxiety ever felt',
        mid_label: 'Uncomfortable but managable',
      },
      {
        id: 'suds-middle',
        type: 'scale',
        heading: 'Anxiety during middle of session',
        min: 0,
        max: 10,
        min_label: 'No anxiety at all',
        max_label: 'Worst anxiety ever felt',
        mid_label: 'Uncomfortable but managable',
      },
      {
        id: 'suds-end',
        type: 'scale',
        heading: 'Anxiety at end of session',
        min: 0,
        max: 10,
        min_label: 'No anxiety at all',
        max_label: 'Worst anxiety ever felt',
        mid_label: 'Uncomfortable but managable',
      },
      {
        id: 'duration',
        type: 'number',
        heading: 'How long was the exposure in minutes?',
        min: 0,
        max: 1440,
      },
      {
        id: 'notes',
        type: 'text_input',
        heading: 'Notes',
        subheading: '',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.348Z',
    updated_at: '2024-03-20T22:45:29.348Z',
  },
  {
    id: 't-00000S',
    slug: 'dbt-distress-tolerance-pros-and-cons',
    title: 'DBT Distress Tolerance: Pros and Cons',
    description:
      'This exercise is a helpful guide when experiencing distress and strong impulses to do something that might make you feel worse in the future.',
    technical_description:
      'This exercise is adapted from Dialectical Behavioral Therapy (DBT) and can be used in conjunction with DBT or as a standalone exercise. It is most helpful for clients who struggle with resisting impulsive urges.\n\n### References:\n- Linehan, M. M. (2015). DBT® skills training manual (2nd ed.). Guilford Press.\n- Valentine, S. E., Bankoff, S. M., Poulin, R. M., Reidler, E. B., & Pantalone, D. W. (2015). The use of dialectical behavior therapy skills training as stand‐alone treatment: A systematic review of the treatment outcome literature. Journal of clinical psychology, 71(1), 1-20.',
    type: 'exercise',
    tags: ['DBT'],
    blocks: [
      {
        id: 'info',
        type: 'reading',
        heading: '',
        content:
          'Complete this exercise and use it as a reminder when you find yourself experiencing an intense urge to do something that will likely make things worse in the future.\n\n**Before the strong urge arrives:** Remind yourself of these pros and cons to your actions.\n\n**When the strong urge arrives:** Imagine the benefits of resisting the urge and the cons of giving in to the urge.',
      },
      {
        id: 'giving-in',
        type: 'group',
        heading: 'Acting on your impulsive urge or giving in to it',
        blocks: [
          {
            id: 'pros',
            type: 'text_input',
            heading: 'Pros',
            subheading: '',
          },
          {
            id: 'cons',
            type: 'text_input',
            heading: 'Cons',
            subheading: '',
          },
        ],
      },
      {
        id: 'resisting',
        type: 'group',
        heading:
          'Resisting your impulsive urge, doing what you need and not quitting',
        blocks: [
          {
            id: 'pros',
            type: 'text_input',
            heading: 'Pros',
            subheading: '',
          },
          {
            id: 'cons',
            type: 'text_input',
            heading: 'Cons',
            subheading: '',
          },
        ],
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.349Z',
    updated_at: '2024-03-20T22:45:29.349Z',
  },
  {
    id: 't-00000T',
    slug: 'dbt-distress-tolerance-improve-the-moment',
    title: 'DBT Distress Tolerance - IMPROVE the moment',
    description:
      'Use these skills when you are experiencing intense distress and you cannot immediately make the situation better.',
    technical_description:
      'This exercise is adapted from Dialectical Behavioral Therapy (DBT) and can be used in conjunction with DBT or as a standalone exercise, but is not meant to be a permanent solution to client issues. It is most helpful for clients who struggle with resisting impulsive urges.\n\n### References:\n- Linehan, M. M. (2015). DBT® skills training manual (2nd ed.). Guilford Press.\n- Valentine, S. E., Bankoff, S. M., Poulin, R. M., Reidler, E. B., & Pantalone, D. W. (2015). The use of dialectical behavior therapy skills training as stand‐alone treatment: A systematic review of the treatment outcome literature. Journal of clinical psychology, 71(1), 1-20.',
    type: 'education',
    tags: ['DBT'],
    blocks: [
      {
        id: 'concepts',
        type: 'reading',
        heading: '',
        content:
          '## Imagery - Imagine somewhere very relaxing\n- Imagine a fantasy world that is calming\n- Think of a happy time in the past and imagine yourself there again\n## Meaning\n- Identify meaning or purpose in a difficult situation\n - Find positive aspects of a painful situation and mentally repeat them\n- Remind yourself of spiritual values and read or listen to spiritual works\n## Prayer\n- Ask for strength from a higher being\n- Open your heart to God, the universe, or a higher power\n## Relaxing Actions\n- Take deep breaths\n- Sit in a hot tub\n- Drink a hot or cold beverage depending on the season\n- Stretch or practice yoga\n- Change the expression on your face\n## One Focus in the Moment\n- Focus your attention on this moment\n- Focus your attention on a physical sensation such as your breath or a single body part\n## Vacation for a Short Time\n- Take an afternoon off\n- Lay in bed for a short while\n- Stay away from devices for a while\n- Go for a walk outside\n## Encourage yourself to Rethink the Situation\n- Say positive affirmations aloud to yourself\n- Repeat "I can do this", "I can make it" several times',
      },
      {
        id: 'takeaways',
        type: 'text_input',
        heading: 'Takeaways',
        subheading: 'Which IMPROVE skills can you use?',
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.349Z',
    updated_at: '2024-03-20T22:45:29.349Z',
  },
  {
    id: 't-00000U',
    slug: 'haters-gonna-hate-handling-criticism',
    title: 'Haters Gonna Hate: Handling Criticism',
    description:
      'Use this skill when you are handling criticism to which you have a strong emotional reaction.',
    technical_description: null,
    type: 'exercise',
    tags: ['Managing Emotions'],
    blocks: [
      {
        id: 'situation',
        type: 'text_input',
        heading: 'What was the situation?',
        subheading: 'e.g. I received harsh feedback on a paper I toiled on',
      },
      {
        id: 'multi_emotions',
        type: 'multi_entry',
        heading: 'What emotions did you experience?',
        blocks: [
          [
            {
              id: 'emotion_select',
              type: 'select',
              heading: 'Choose an emotion',
              options: {
                Happiness: [
                  'Happy',
                  'Satisfied',
                  'Curious',
                  'Proud',
                  'Confident',
                  'Respected',
                  'Powerful',
                  'Thankful',
                  'Peaceful',
                  'Loving',
                  'Trusting',
                  'Hopeful',
                  'Inspired',
                ],
                Sadness: [
                  'Sad',
                  'Lonely',
                  'Vulnerable',
                  'Powerless',
                  'Grief',
                  'Hopeless',
                  'Regretful',
                  'Guilty',
                  'Ashamed',
                  'Empty',
                ],
                Fear: [
                  'Anxious',
                  'Scared',
                  'Overwhelmed',
                  'Helpless',
                  'Insecure',
                  'Worthless',
                  'Excluded',
                  'Nervous',
                  'Threatened',
                ],
                Anger: [
                  'Angry',
                  'Resentful',
                  'Furious',
                  'Jealous',
                  'Hostile',
                  'Annoyed',
                  'Frustrated',
                  'Bitter',
                  'Skeptical',
                  'Numb',
                ],
                Surprise: [
                  'Surprised',
                  'Excited',
                  'Shocked',
                  'Amazed',
                  'Confused',
                ],
                Disgust: [
                  'Embarrassed',
                  'Humiliated',
                  'Self-Conscious',
                  'Insecure',
                  'Disappointed',
                ],
                Other: ['Bad', 'Tired', 'Overwhelmed', 'Bored'],
              },
            },
            {
              id: 'emotion_strength',
              type: 'scale',
              heading: 'Intensity of emotion',
              min: 1,
              max: 10,
              min_label: 'Very weak',
              max_label: 'Very strong',
              mid_label: '',
            },
          ],
        ],
      },
      {
        id: 'thoughts',
        type: 'text_input',
        heading: 'What thoughts did you have about the situation?',
        subheading: "e.g. I'll never get this right.",
      },
    ],
    is_bookmarked: null,
    author_id: null,
    is_public: true,
    created_at: '2024-03-20T22:45:29.349Z',
    updated_at: '2024-03-20T22:45:29.349Z',
  },
];

class MockApi {
  get(url: string) {
    if (url.startsWith('/api/v1/templates')) {
      if (url == '/api/v1/templates/') {
        return TEMPLATES;
      } else if (url.startsWith('/api/v1/templates/by_slug/')) {
        const slug = url.split('/').pop();
        const template = TEMPLATES.find((template) => template.slug === slug);
        if (template) {
          return template;
        } else {
          throw new Error('Template not found');
        }
      }
    }

    throw new Error('Invalid URL');
  }
}

export default new MockApi();
